import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";

const portalRoot = typeof document !== "undefined" ? document.body : null;

const toneClasses = {
  auto: "tw-bg-white tw-text-slate-900 tw-border tw-border-slate-200 dark:tw-bg-slate-900/95 dark:tw-text-white dark:tw-border-slate-700",
  dark: "tw-bg-slate-900/95 tw-text-white tw-border tw-border-slate-800",
  light: "tw-bg-white tw-text-slate-900 tw-border tw-border-slate-200",
};

const transforms = {
  top: "translate(-50%, -100%) translateY(-12px)",
  bottom: "translate(-50%, 0) translateY(12px)",
  left: "translate(-100%, -50%) translateX(-12px)",
  right: "translate(0, -50%) translateX(12px)",
};

const ModernTooltip = ({
  label,
  children,
  placement = "top",
  tone = "auto",
  maxWidth = 280,
  className = "",
  as: Wrapper = "span",
  wrapperClassName = "tw-inline-flex tw-items-center tw-gap-1 tw-align-middle",
  disabled = false,
}) => {
  const triggerRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || typeof window === "undefined") return;

    const rect = triggerRef.current.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;

    let top = rect.top + scrollY;
    let left = rect.left + scrollX;

    switch (placement) {
      case "bottom":
        top = top + rect.height;
        left = left + rect.width / 2;
        break;
      case "left":
        top = top + rect.height / 2;
        break;
      case "right":
        top = top + rect.height / 2;
        left = left + rect.width;
        break;
      case "top":
      default:
        left = left + rect.width / 2;
        break;
    }

    setCoords({ top, left });
  }, [placement]);

  useEffect(() => {
    if (!visible) return undefined;

    calculatePosition();
    window.addEventListener("scroll", calculatePosition, true);
    window.addEventListener("resize", calculatePosition);

    return () => {
      window.removeEventListener("scroll", calculatePosition, true);
      window.removeEventListener("resize", calculatePosition);
    };
  }, [visible, calculatePosition]);

  const handleShow = () => {
    if (!disabled) setVisible(true);
  };

  const handleHide = () => {
    setVisible(false);
  };

  const isVertical = placement === "top" || placement === "bottom";

  const content =
    visible && portalRoot
      ? createPortal(
          <div
            className={`tw-pointer-events-none tw-fixed tw-z-[9999] ${
              isVertical ? "tw-flex tw-flex-col tw-items-center" : "tw-flex tw-items-center"
            } tw-text-sm tw-font-medium ${className}`}
            style={{
              top: coords.top,
              left: coords.left,
              transform: transforms[placement] || transforms.top,
              maxWidth,
            }}
          >
            <div
              className={`tw-rounded-2xl tw-px-4 tw-py-2.5 tw-shadow-2xl tw-ring-1 tw-backdrop-blur-md ${
                toneClasses[tone] || toneClasses.auto
              }`}
            >
              {label}
            </div>
          </div>,
          portalRoot
        )
      : null;

  return (
    <>
      <Wrapper
        ref={triggerRef}
        className={wrapperClassName}
        onMouseEnter={handleShow}
        onMouseLeave={handleHide}
        onFocus={handleShow}
        onBlur={handleHide}
      >
        {children}
      </Wrapper>
      {content}
    </>
  );
};

ModernTooltip.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  children: PropTypes.node.isRequired,
  placement: PropTypes.oneOf(["top", "bottom", "left", "right"]),
  tone: PropTypes.oneOf(["auto", "dark", "light"]),
  maxWidth: PropTypes.number,
  className: PropTypes.string,
  as: PropTypes.elementType,
  wrapperClassName: PropTypes.string,
  disabled: PropTypes.bool,
};

export default ModernTooltip;
